import React from 'react';
import { Language } from '../types';
import { getSiteConfig } from '../data/siteConfig';
import { Phone, MessageCircle, FileText } from 'lucide-react';

interface MobileQuickBarProps {
  currentLang: Language;
  onNavigateContact: () => void;
}

export const MobileQuickBar: React.FC<MobileQuickBarProps> = ({ currentLang, onNavigateContact }) => {
  const config = getSiteConfig();
  const rawPhone = (config.gsm || '+32493373789').replace(/[^0-9+]/g, '');

  const whatsappMessages = {
    fr: "Bonjour API HEAT, j'ai une urgence (fuite / chaudière / débouchage) et j'ai besoin d'une intervention rapide en Belgique.",
    nl: "Hallo API HEAT, ik heb een noodgeval (lek / ketel / verstopping) en heb snel een interventie nodig in België.",
    en: "Hello API HEAT, I have an emergency (leak / boiler / unblocking) and need a quick call-out in Belgium."
  };

  const whatsappUrl = `https://wa.me/32493373789?text=${encodeURIComponent(whatsappMessages[currentLang])}`;

  const labels = {
    fr: {
      call: 'Appel 24/7',
      whatsapp: 'WhatsApp',
      quote: 'Devis Rapide'
    },
    nl: {
      call: 'Bellen 24/7',
      whatsapp: 'WhatsApp',
      quote: 'Offerte'
    },
    en: {
      call: 'Call 24/7',
      whatsapp: 'WhatsApp',
      quote: 'Fast Quote'
    }
  }[currentLang];

  return (
    <aside
      aria-label={labels.call}
      className="fixed bottom-0 left-0 right-0 z-40 lg:hidden bg-slate-950/95 backdrop-blur-md border-t border-slate-800 px-3 py-2 shadow-2xl safe-area-pb"
    >
      <div className="flex items-center justify-between gap-2 max-w-md mx-auto">
        {/* Direct Call Button (Dominant CTA for mobile urgency) */}
        <a
          href={`tel:${rawPhone}`}
          aria-label={`Appeler le service d'urgence 24/7 au ${rawPhone}`}
          className="flex-1 min-h-[48px] flex items-center justify-center gap-2 bg-gradient-to-r from-red-600 to-rose-600 active:from-red-700 active:to-rose-700 text-white font-extrabold py-3 px-3 rounded-xl shadow-lg shadow-red-950/50 text-xs sm:text-sm tracking-wide transition-transform active:scale-95"
        >
          <Phone className="w-4 h-4 fill-current animate-pulse text-yellow-300 shrink-0" aria-hidden="true" />
          <span className="truncate">{labels.call}</span>
        </a>

        {/* WhatsApp Button */}
        <a
          href={whatsappUrl}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Contacter par WhatsApp"
          className="flex-1 min-h-[48px] flex items-center justify-center gap-1.5 bg-[#25D366] hover:bg-[#20ba5a] text-white font-bold py-3 px-2.5 rounded-xl shadow-md text-xs sm:text-sm tracking-wide transition-transform active:scale-95"
        >
          <MessageCircle className="w-4 h-4 fill-current shrink-0" aria-hidden="true" />
          <span className="truncate">{labels.whatsapp}</span>
        </a>

        {/* Quick Quote Button */}
        <button
          onClick={onNavigateContact}
          aria-label="Demander un devis rapide en ligne"
          className="flex-1 min-h-[48px] flex items-center justify-center gap-1.5 bg-slate-800 hover:bg-slate-700 text-slate-100 font-bold py-3 px-2.5 rounded-xl border border-slate-700 text-xs sm:text-sm tracking-wide transition-transform active:scale-95"
        >
          <FileText className="w-4 h-4 text-orange-400 shrink-0" aria-hidden="true" />
          <span className="truncate">{labels.quote}</span>
        </button>
      </div>
    </aside>
  );
};
